import type { ContextType } from "react";
import { ReactNode } from "react";
import { LicenseDetails, LicenseService } from "../licensing/LicenseService";
import Authenticator from "../models/Authenticator";
import { UserDetailsSetStateContext } from "./login/UserDetailsProvider";
import LoginFormInput from "./LoginFormInput";
import SubscribingComponent from "./SubscribingComponent";
import { LoginStatus } from "../models/LoginStatus";
import { Button } from "./buttons/Button";
import { Logo } from "./Logo";
import { LoginMessages } from "../models/messages/Messages";
import "../styles/fonts.css";
import "../styles/index.css";
import "../styles/loginForm.css";
import "../styles/panel.css";
import { flushSync } from "react-dom";

export { LoginForm };

type Props = {
  children: ReactNode;
  version: string;
  disableHeartBeat?: boolean;
  loggedIn: VoidFunction;
};

type State = {
  status:
    | "loggedOut"
    | "loggedIn"
    | "incorrectCredentials"
    | "serverError"
    | "authenticating"
    | "firstCheck";
  errors: string[];
  username: string;
  password: string;
  showPassword: boolean;
  licenseDetails?: LicenseDetails;
  showPageCountWarning: boolean;
  usage: number;
  pageCountMax: number;
};

class LoginError implements Error {
  name: string;
  message: string;
  constructor(title: string, msg: string) {
    this.name = title;
    this.message = msg;
  }
  stack?: string | undefined;
}

class LoginForm extends SubscribingComponent<Props, State> {
  heartbeatInterval: NodeJS.Timeout | null = null;
  authenticator: Authenticator;
  static contextType = UserDetailsSetStateContext;
  declare context: ContextType<typeof UserDetailsSetStateContext>;

  constructor(props: Readonly<Props>) {
    super(props);
    this.authenticator = new Authenticator();
    this.state = {
      status: "firstCheck",
      username: "", // get from LocalStorage
      password: "",
      errors: [],
      showPassword: false,
      showPageCountWarning: false,
      licenseDetails: undefined,
      usage: 0,
      pageCountMax: 0,
    };
  }

  setStateSync: typeof this.setState = (...args) => {
    flushSync(() => {
      this.setState(...args);
    });
  };

  componentDidMount() {
    try {
      // Open the url http://example.org/accelerator/#?_jwt=jwt.token.details
      const params = new URLSearchParams(window.location.hash.substring(1));
      if (params.has("_jwt")) {
        this.authenticator.setJwtAuth(params.get("_jwt")!);
      }
    } catch (e) {
      console.error("Could not find JWT", e);
    }
    // LicenseService.getPageUsage().then(usage => {
    //     LicenseService.getLicenseDetails().then(details => this.setStateSync({
    //         showPageCountWarning: (details.pageCount - (usage ?? 0)) <= 1000,
    //         usage: usage ?? 0,
    //         pageCountMax: details.pageCount,
    //     }))
    // }).catch(e => console.log(e));
    try {
      this.authenticator
        .amLoggedIn()
        .then((loginStatus) => {
          if (loginStatus?.isauthenticated) {
            this.checkLoginStatus();
          } else {
            this.setStateSync({
              status: "loggedOut",
            });
          }
        })
        .catch(console.warn);
    } catch (e) {
      console.warn(e);
    }
    // subscribe to logout/expired login token
    this.subscribe("authenticationError", () => {
      this.setStateSync({
        status: "loggedOut",
      });
    });
  }

  async checkLoginStatus() {
    try {
      const loginStatus = await this.authenticator.amLoggedIn();
      if (loginStatus?.isauthenticated) {
        await this.postLogin(loginStatus);
      }
    } catch (e) {
      const err = e as LoginError;
      this.setStateSync({
        status: "serverError",
        errors: err.message.split("\n"),
      });
      return false;
    }
    return true;
  }

  async postLogin(loginStatus: LoginStatus | null) {
    if (loginStatus == null) throw new LoginError("Login Failed", "");
    // set user details once it's confirmed we've authenticated
    this.context!({
      username: loginStatus.name,
      admin: loginStatus.isadmin === "true",
    });
    // get Roles update status
    const gotRoles = await this.authenticator.getCurrentRoles();
    if (!gotRoles) {
      throw new LoginError(
        "Server Error",
        LoginMessages.serverRespondedWrong(),
      );
    }
    this.setStateSync({
      status: "loggedIn",
    });
  }

  UNSAFE_componentWillUpdate(nextProps: Props, nextState: State) {
    if (this.state.status !== nextState.status) {
      if (nextState.status === "loggedIn") {
        this.heartbeatInterval = this.buildHeartbeatInterval();
        nextProps.loggedIn();
      } else if (nextState.status === "loggedOut") {
        this.clearHeartbeatInterval();
      }
    }
  }

  async login() {
    if (this.state.status === "authenticating") return;
    this.setStateSync({
      status: "authenticating",
    });
    // login
    try {
      const loggedIn = await new Authenticator().login(
        this.state.username,
        this.state.password,
      );
      if (loggedIn) {
        this.checkLoginStatus();
      } else {
        this.setStateSync({
          status: "incorrectCredentials",
          errors: ["Incorrect Credentials", LoginMessages.wrongCredentials()],
        });
      }
    } catch (e) {
      this.setStateSync({
        status: "loggedOut",
      });
    }
  }

  clearHeartbeatInterval(): void {
    if (!this.props.disableHeartBeat && this.heartbeatInterval != null) {
      clearTimeout(this.heartbeatInterval);
    }
  }

  buildHeartbeatInterval(): NodeJS.Timeout | null {
    let heartBeatInterval = null;
    if (!this.props.disableHeartBeat) {
      heartBeatInterval = setInterval(() => {
        new Authenticator().amLoggedIn();
      }, 60 * 1000);
    }
    return heartBeatInterval;
  }

  render() {
    const showLoginScreen = !["firstCheck", "loggedIn"].includes(
      this.state.status,
    );
    const error: boolean =
      ["incorrectCredentials", "serverError"].indexOf(this.state.status) > -1;
    return (
      <>
        {this.state.status === "firstCheck" && <br />}
        {this.state.status === "loggedIn" && this.props.children}
        {showLoginScreen && (
          <div className={"loginFormWrapper"}>
            {/* {this.state.showPageCountWarning && (
                            <div>
                                Page Usage: {this.state.usage} / {this.state.pageCountMax}
                            </div>
                        )} */}

            <div className={`panel loginForm ${error ? "hasError" : ""}`}>
              <div className={"header"}>
                <span>Login</span>
              </div>
              <div
                className={"body"}
                style={{
                  flexGrow: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div className={"headerImage"}>
                  <Logo />
                </div>
                {/* Username Input */}
                <LoginFormInput
                  label={"Username"}
                  tabIndex={0}
                  initialValue={this.state.username}
                  onChange={(data: string) =>
                    this.setStateSync({ username: data })
                  }
                  autoFocus
                  autoComplete={"username"}
                  onSubmit={() =>
                    this.state.password !== "" ? this.login() : null
                  }
                />
                {/* Password Input */}
                <LoginFormInput
                  label={"Password"}
                  tabIndex={0}
                  password={true && !this.state.showPassword}
                  initialValue={this.state.password}
                  onChange={(data: string) =>
                    this.setStateSync({ password: data })
                  }
                  autoComplete={"current-password"}
                  onSubmit={() => this.login()}
                />
                <div className={"errors"}>
                  <div className={"header"}>Login Failed</div>
                  {this.state.errors.map((s, i) => (
                    <div className={"message"} key={s + i}>
                      {s}
                    </div>
                  ))}
                </div>
                <div className={"signIn"}>
                  <Button
                    className={"signIn"}
                    tabIndex={0}
                    disabled={this.state.status === "authenticating"}
                    onClick={() => this.login()}
                  >
                    {["authenticating", "firstCheck"].includes(
                      this.state.status,
                    )
                      ? "Logging In"
                      : "Sign In"}
                  </Button>
                </div>
                <div className={"copyright"}>
                  <span>&#169; Future State Systems</span>
                  <span>{`Version: ${this.props.version}`}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }
}
